import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, NavLink, Link, useLocation } from 'react-router-dom';
import {
  ShieldCheckIcon,
  HomeIcon,
  TableCellsIcon,
  PlusCircleIcon,
  BeakerIcon,
  ArrowRightStartOnRectangleIcon,
  ServerStackIcon,
  CubeIcon,
  EyeSlashIcon,
  BuildingOffice2Icon,
  BookOpenIcon,
  SparklesIcon,
  BellIcon,
  AdjustmentsHorizontalIcon,
  NoSymbolIcon,
  ChartBarSquareIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowsRightLeftIcon,
  HeartIcon,
  ClockIcon,
  EnvelopeIcon,
  TicketIcon,
  MapIcon,
  LinkIcon,
  FingerPrintIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  SignalIcon,
  LockClosedIcon,
  QuestionMarkCircleIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import HomePage from './components/HomePage';
import CaseDashboard from './components/CaseDashboard';
import CaseCreate from './components/CaseCreate';
import CaseDetail from './components/CaseDetail';
import ServiceIPManager from './components/ServiceIPManager';
import KnownAppManager from './components/KnownAppManager';
import AitmTrustedDomainsManager from './components/AitmTrustedDomainsManager';
import AitmTrustedPairsManager from './components/AitmTrustedPairsManager';
import KnownMaliciousIPsManager from './components/KnownMaliciousIPsManager';
import MailboxPairAllowlistManager from './components/MailboxPairAllowlistManager';
import AccountAllowlistManager from './components/AccountAllowlistManager';
import TeamsPhoneModelsManager from './components/TeamsPhoneModelsManager';
import DefenderFileHashAllowlistManager from './components/DefenderFileHashAllowlistManager';
import DefenderPassthroughIpAllowlistManager from './components/DefenderPassthroughIpAllowlistManager';
import SprayOriginIPWatchlistManager from './components/SprayOriginIPWatchlistManager';
import UserAppPairAllowlistManager from './components/UserAppPairAllowlistManager';
import GroupAppAllowlistManager from './components/GroupAppAllowlistManager';
import MspNotificationSettingsManager from './components/MspNotificationSettingsManager';
import TicketingConfigManager from './components/TicketingConfigManager';
import PortalUserPsaMappingManager from './components/PortalUserPsaMappingManager';
import TenantTicketingRoutingManager from './components/TenantTicketingRoutingManager';
import WebhookConfigManager from './components/WebhookConfigManager';
import IpActivitySearchResultsPage from './components/IpActivitySearchResultsPage';
import LoginPage from './components/LoginPage';
import AccessDenied from './components/AccessDenied';
import ServiceUnavailable from './components/ServiceUnavailable';
import ConnectionErrorBanner from './components/ConnectionErrorBanner';
import TenantManagement from './components/TenantManagement';
import AdminTenantsHub from './components/AdminTenantsHub';
import BpcTenantsManagement from './components/BpcTenantsManagement';
import HiddenOperations from './components/HiddenOperations';
import InvestigatorInstructions from './components/InvestigatorInstructions';
import ReleaseNotes from './components/ReleaseNotes';
import GuideHome from './components/guide/GuideHome';
import AlertsGuide from './components/guide/AlertsGuide';
import { availableGuideSections } from './components/guide/guideSections';
import AlertsPage from './components/AlertsPage';
import RecentEventsPage from './components/RecentEventsPage';
import IpAccountSearchPage from './components/IpAccountSearchPage';
import AlertRulesPage from './components/AlertRulesPage';
import TestRunResultsPage from './components/TestRunResultsPage';
import CoverageAnalysisPage from './components/CoverageAnalysisPage';
import CoverageHub from './components/CoverageHub';
import EntraIdentityPage from './components/EntraIdentityPage';
import CoverageComparisonPage from './components/CoverageComparisonPage';
import SloDashboard from './components/SloDashboard';
import ActivityLogPage from './components/ActivityLogPage';
import InvestigationsAvoidedDashboard from './components/InvestigationsAvoidedDashboard';
import TenantCostDashboard from './components/TenantCostDashboard';
import { isUsingMockData } from './api/client';
import { useAuth } from './hooks/useAuth';
import { isDemoMode, setDemoMode } from './utils/demoMode';

// Top-level nav items. `subItemsKey` indicates which sub-nav to render
// indented below this item when the user is in a matching route context.
const navItems = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/cases', label: 'Cases', icon: TableCellsIcon, end: true, subItemsKey: 'cases' },
  { to: '/alerts', label: 'Alerts', icon: BellIcon, subItemsKey: 'alerts' },
  { to: '/recent-events', label: 'Recent Events', icon: ClockIcon, end: true },
  { to: '/ip-search', label: 'IP Search', icon: MagnifyingGlassIcon, end: true },
  { to: '/coverage', label: 'Coverage', subLabel: 'Analysis', icon: ChartBarSquareIcon, end: true, subItemsKey: 'coverage' },
  { to: '/coverage/entra-identity', label: 'Identity Inventory', icon: UserGroupIcon, end: true },
];

const investigationItems = [
  { to: '/cases/new', label: 'New Case', icon: PlusCircleIcon },
  { to: '/settings/service-ips', label: 'Service IPs', icon: ServerStackIcon },
  { to: '/settings/known-apps', label: 'Known Apps', icon: CubeIcon },
  { to: '/settings/hidden-ops', label: 'Hidden Ops', icon: EyeSlashIcon },
];

const alertingItems = [
  { to: '/settings/aitm-trusted-domains', label: 'AiTM Trusted Domains', icon: ShieldCheckIcon },
  { to: '/settings/aitm-trusted-pairs', label: 'AiTM Trusted Pairs', icon: ShieldCheckIcon },
  { to: '/settings/known-malicious-ips', label: 'Known Malicious IPs', icon: NoSymbolIcon },
  { to: '/settings/mailbox-pair-allowlist', label: 'Mailbox-Pair Allowlist', icon: EnvelopeIcon },
  { to: '/settings/account-allowlist', label: 'Account Allowlist', icon: DevicePhoneMobileIcon },
  { to: '/settings/teams-phone-models', label: 'Teams-Phone Models', icon: DevicePhoneMobileIcon, globalAdminOnly: true },
  { to: '/settings/defender-file-hash-allowlist', label: 'Defender File-Hash Allowlist', icon: FingerPrintIcon, globalAdminOnly: true },
  { to: '/settings/user-app-pair-allowlist', label: 'User/App Pair Allowlist', icon: UserGroupIcon },
  { to: '/settings/group-app-allowlist', label: 'Group/App Pair Allowlist', icon: UserGroupIcon },
  { to: '/settings/spray-origin-watchlist', label: 'Spray-Origin IP Watchlist', icon: SignalIcon },
  { to: '/settings/benign-ip-allowlist', label: 'Defender Pass-through IP Allowlist', icon: ShieldCheckIcon, globalAdminOnly: true },
];

const coverageItems = [
  { to: '/coverage/devices',        label: 'Device Inventory',            icon: ComputerDesktopIcon },
  { to: '/coverage/comparison',     label: 'Device Inventory Comparison', icon: ArrowsRightLeftIcon },
];

// User Guide sections. Sourced from components/guide/guideSections so the sidebar
// and the guide landing page can never disagree about what exists.
const guideItems = availableGuideSections.map((s) => ({ to: s.to, label: s.label, icon: s.icon }));

const investigationPaths = new Set(investigationItems.map((i) => i.to));
const alertingPaths = new Set(alertingItems.map((i) => i.to));
const coveragePaths = new Set(coverageItems.map((i) => i.to));

const adminItems = [
  { to: '/admin/tenants', label: 'Tenants', icon: BuildingOffice2Icon },
  { to: '/admin/alert-rules', label: 'Alert Rules', icon: AdjustmentsHorizontalIcon },
  { to: '/admin/msp-notifications', label: 'Alert Email Routing', icon: EnvelopeIcon },
  { to: '/admin/ticketing-config', label: 'PSA Setup', icon: TicketIcon },
  { to: '/admin/tenant-ticketing-routes', label: 'PSA Ticket Routing', icon: MapIcon },
  { to: '/admin/psa-user-mapping', label: 'PSA User Mapping', icon: UserGroupIcon },
  { to: '/admin/webhooks', label: 'Alert Webhooks', icon: LinkIcon },
  { to: '/admin/investigations-avoided', label: 'Investigations Avoided', icon: ShieldCheckIcon },
  { to: '/admin/tenant-cost', label: 'Cost', icon: CurrencyDollarIcon },
  { to: '/admin/slo', label: 'Pipeline Health', icon: HeartIcon },
  { to: '/admin/activity-log', label: 'Activity Log', icon: ClipboardDocumentListIcon },
];

// Admin pages MSP admins (non-global) can use for their own MSPs.
const mspAdminPaths = new Set([
  '/admin/tenants',
  '/admin/ticketing-config',
  '/admin/tenant-ticketing-routes',
  '/admin/psa-user-mapping',
  '/admin/webhooks',
  '/admin/tenant-cost',
]);

// Admin pages any MSP member (readers AND admins, not just MSP admins) can use —
// scoped server-side to their own MSP. Investigations Avoided is value reporting,
// so MSP readers get the same link + their own MSP's data.
const mspMemberPaths = new Set([
  '/admin/investigations-avoided',
]);

const adminTenantsItems = [
  { to: '/admin/tenants/m365', label: 'M365 Tenants',           icon: BuildingOffice2Icon },
  { to: '/admin/tenants/bpc',  label: 'BlackPoint Cyber Tenants', icon: ShieldCheckIcon },
];

// Demo Mode is read once per page load — every toggle reloads the page, so the
// api/client.js module-scope flag and this one can never disagree.
const DEMO_ON = isDemoMode();

// Seconds the "Exit Demo Mode" countdown runs before real data reappears, so a
// presenter can't un-mask customer data while still screen sharing.
const EXIT_DEMO_COUNTDOWN_SECONDS = 30;

const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 400;
const SIDEBAR_DEFAULT = 256;
const SIDEBAR_WIDTH_KEY = 'm365signal-sidebar-width';

function getInitialSidebarWidth() {
  try {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= SIDEBAR_MIN && val <= SIDEBAR_MAX) return val;
    }
  } catch { /* ignore */ }
  return SIDEBAR_DEFAULT;
}

export default function App() {
  const [showMockBanner, setShowMockBanner] = useState(false);
  const { user, loading, isAuthenticated, login, logout, authProgress } = useAuth();
  const [sidebarWidth, setSidebarWidth] = useState(getInitialSidebarWidth);
  const location = useLocation();
  const isOnCasePage = /^\/cases\/(?!new\b)/.test(location.pathname);

  // Context-aware sidebar groups: when working on cases, only show Investigation
  // settings; when working on alerts, only show Alerting settings; on neutral
  // routes (Home, Tenants, Release Notes), show both. The settings pages
  // themselves count as their own context so the active link stays visible.
  const path = location.pathname;
  const inInvestigationsContext =
    path.startsWith('/cases') || investigationPaths.has(path);
  const inAlertingContext =
    path.startsWith('/alerts') ||
    path.startsWith('/admin/alert-rules') ||
    path.startsWith('/admin/test-results') ||
    alertingPaths.has(path);
  // Identity Inventory (/coverage/entra-identity) is now a top-level nav item, so
  // it must NOT count as coverage context — otherwise the Device Inventory / Comparison
  // sub-menu would expand under "Coverage Analysis" while viewing Identity Inventory.
  const inCoverageContext =
    (path.startsWith('/coverage') || coveragePaths.has(path)) &&
    path !== '/coverage/entra-identity';

  // Sub-nav resolution: each parent navItem with `subItemsKey` exposes its
  // children indented below when the user is in a matching context.
  const inGuideContext = path.startsWith('/guide');

  const subItemsMap = {
    cases:    { items: investigationItems, show: inInvestigationsContext },
    alerts:   { items: alertingItems,      show: inAlertingContext },
    coverage: { items: coverageItems,      show: inCoverageContext },
    guide:    { items: guideItems,         show: inGuideContext },
  };
  const [showInstructions, setShowInstructions] = useState(false);
  const [showExitDemo, setShowExitDemo] = useState(false);
  const isDragging = useRef(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const newWidth = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, e.clientX));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // Persist on drag end
      setSidebarWidth((w) => {
        try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w)); } catch { /* ignore */ }
        return w;
      });
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // Auto-close instructions panel when navigating away from a case
  useEffect(() => {
    if (!isOnCasePage) setShowInstructions(false);
  }, [isOnCasePage]);

  useEffect(() => {
    const timer = setTimeout(() => setShowMockBanner(isUsingMockData()), 1500);
    const interval = setInterval(() => setShowMockBanner(isUsingMockData()), 5000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, []);

  // Show loading spinner while auth state is being determined. When /api/me is
  // being retried (useAuth's bounded fetch, v1.184.2) say so — a bare spinner
  // for 20+ s reads as "down", and the user's reload then queues another request
  // behind the same stalled API worker.
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        {authProgress && (
          <p className="text-sm text-amber-300 text-center max-w-sm px-6" role="status">
            The portal service is slow to respond — retrying
            (attempt {authProgress.nextAttempt} of {authProgress.attempts})…
          </p>
        )}
      </div>
    );
  }

  // Show login page if not authenticated (skip in local dev where /.auth/me is unavailable)
  if (!isAuthenticated && import.meta.env.PROD) {
    return <LoginPage onLogin={login} />;
  }

  // Show a retry screen when we could not reach /api/me (network/5xx/host stall)
  // — we could NOT ask whether the user is entitled, so we must not tell them
  // they are denied. Must come before the Access Denied check, since authGate
  // 'unreachable' also makes isAuthorized false.
  if (isAuthenticated && user.authGate === 'unreachable') {
    return <ServiceUnavailable onLogout={logout} />;
  }

  // Show access denied only when /api/me gave an authoritative answer that the
  // user is genuinely not entitled (200 with no MSP + not global admin, or a
  // 401/403 from the auth layer) — never for an unreachable API.
  if (isAuthenticated && !user.isAuthorized) {
    return <AccessDenied userEmail={user.userDetails} onLogout={logout} />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <ConnectionErrorBanner />
      {/* Sidebar */}
      <aside
        className="flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col relative"
        style={{ width: sidebarWidth }}
      >
        {/* Logo / Brand */}
        <Link
          to="/"
          className="flex items-center gap-3 px-6 py-5 border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
        >
          <ShieldCheckIcon className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-sm font-bold text-gray-100 leading-tight">
              Blue365Signal
            </h1>
            <p className="text-xs text-gray-500 leading-tight">
              Security Investigation Portal
            </p>
          </div>
        </Link>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const sub = item.subItemsKey ? subItemsMap[item.subItemsKey] : null;
            return (
              <React.Fragment key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    }`
                  }
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="flex flex-col leading-tight">
                    {item.label}
                    {item.subLabel && <span className="text-sm font-normal opacity-70">{item.subLabel}</span>}
                  </span>
                </NavLink>
                {sub && sub.show && (
                  <div className="ml-4 mt-0.5 mb-1 border-l border-gray-800 pl-2 space-y-0.5">
                    {sub.items.filter((subItem) => !subItem.globalAdminOnly || user?.isGlobalAdmin).map((subItem) => (
                      <NavLink
                        key={subItem.to}
                        to={subItem.to}
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                            isActive
                              ? 'bg-blue-600/15 text-blue-300'
                              : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                          }`
                        }
                      >
                        <subItem.icon className="h-4 w-4 flex-shrink-0" />
                        {subItem.label}
                        {subItem.globalAdminOnly && (
                          <span title="Global admin only" className="ml-auto flex-shrink-0">
                            <LockClosedIcon className="h-3 w-3 text-amber-400/80" />
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {isOnCasePage && (
            <div className="pt-4 mt-4 border-t border-gray-800">
              <p className="px-3 mb-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Investigation
              </p>
              <button
                onClick={() => setShowInstructions((v) => !v)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  showInstructions
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`}
              >
                <BookOpenIcon className="h-5 w-5 flex-shrink-0" />
                Steps
              </button>
            </div>
          )}

          <div className="pt-4 mt-4 border-t border-gray-800">
            {/* User Guide — sits with Release Notes in the utility block: content
                about the product rather than customer data, visible from every
                page and to every role. */}
            <NavLink
              to="/guide"
              end
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`
              }
            >
              <QuestionMarkCircleIcon className="h-5 w-5 flex-shrink-0" />
              User Guide
            </NavLink>
            {inGuideContext && (
              <div className="ml-4 mt-0.5 mb-1 border-l border-gray-800 pl-2 space-y-0.5">
                {guideItems.map((subItem) => (
                  <NavLink
                    key={subItem.to}
                    to={subItem.to}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-600/15 text-blue-300'
                          : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                      }`
                    }
                  >
                    <subItem.icon className="h-4 w-4 flex-shrink-0" />
                    {subItem.label}
                  </NavLink>
                ))}
              </div>
            )}

            <NavLink
              to="/release-notes"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`
              }
            >
              <SparklesIcon className="h-5 w-5 flex-shrink-0" />
              Release Notes
            </NavLink>
          </div>

          {(user?.isGlobalAdmin || user?.isMspAdmin || (user?.mspCodes?.length > 0)) && (
            <div className="pt-4 mt-4 border-t border-gray-800">
              <p className="px-3 mb-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Administration
              </p>
              {/* global admins see all; MSP admins see their MSP-scoped admin pages; any MSP member
                  (incl. non-admin readers) sees the MSP-member pages (e.g. Investigations Avoided). */}
              {adminItems
                .filter((item) =>
                  user?.isGlobalAdmin ||
                  (user?.isMspAdmin && mspAdminPaths.has(item.to)) ||
                  ((user?.mspCodes?.length > 0) && mspMemberPaths.has(item.to))
                )
                .map((item) => (
                <React.Fragment key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/admin/tenants'}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {item.label}
                  </NavLink>
                  {/* Sub-nav for /admin/tenants/* */}
                  {item.to === '/admin/tenants' && path.startsWith('/admin/tenants') && (
                    <div className="ml-4 mt-0.5 mb-1 border-l border-gray-800 pl-2 space-y-0.5">
                      {adminTenantsItems.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                              isActive
                                ? 'bg-blue-600/15 text-blue-300'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                            }`
                          }
                        >
                          <sub.icon className="h-4 w-4 flex-shrink-0" />
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </nav>

        {/* User & Footer */}
        <div className="px-6 py-4 border-t border-gray-800">
          {user && (
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-gray-400 truncate" title={user.userDetails}>
                {user.userDetails}
              </p>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-400 transition-colors ml-2 flex-shrink-0"
                title="Sign out"
              >
                <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          {/* Demo Mode toggle — global admins only. Entering is instant;
              exiting goes through a confirmation + countdown (ExitDemoModal). */}
          {(user?.isGlobalAdmin || DEMO_ON) && (
            <button
              type="button"
              onClick={() => {
                if (DEMO_ON) { setShowExitDemo(true); return; }
                setDemoMode(true);
                window.location.reload();
              }}
              title={DEMO_ON
                ? 'Turn off Demo Mode and show real customer data again'
                : 'Anonymize all customer data and make the portal read-only, for live prospect demos'}
              className={`mb-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded border text-xs font-medium transition-colors ${
                DEMO_ON
                  ? 'bg-amber-600/20 border-amber-500/50 text-amber-300 hover:bg-amber-600/30'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
              }`}
            >
              <BeakerIcon className="h-3.5 w-3.5 flex-shrink-0" />
              {DEMO_ON ? 'Exit Demo Mode' : 'Demo Mode'}
            </button>
          )}
          <p className="text-xs text-gray-600">
            Blue365Signal Portal v{__APP_VERSION__}
          </p>
        </div>
        {/* Resize handle */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-blue-500/40 active:bg-blue-500/60 transition-colors z-10"
        />
      </aside>

      {/* Instructions Panel */}
      {showInstructions && (
        <div className="flex-shrink-0 w-72 overflow-hidden">
          <InvestigatorInstructions onClose={() => setShowInstructions(false)} />
        </div>
      )}

      {showExitDemo && <ExitDemoModal onClose={() => setShowExitDemo(false)} />}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-gray-950">
        {/* Rendered whenever the flag is set, regardless of role — an honest
            banner matters more than role gating if the key is hand-set. */}
        {DEMO_ON && (
          <div className="sticky top-0 z-40 bg-amber-600 px-4 py-1.5 flex items-center justify-center gap-2 shadow-lg">
            <BeakerIcon className="h-4 w-4 text-black flex-shrink-0" />
            <span className="text-xs font-bold text-black tracking-widest uppercase">
              Demo Mode — data anonymized · portal is read-only
            </span>
          </div>
        )}
        {import.meta.env.VITE_ENVIRONMENT === 'dev' && (
          <div className="bg-amber-600 px-4 py-1 text-center">
            <span className="text-xs font-bold text-black tracking-widest uppercase">
              Dev Environment
            </span>
          </div>
        )}
        {showMockBanner && (
          <div className="bg-amber-900/40 border-b border-amber-700/50 px-4 py-2 flex items-center justify-center gap-2">
            <BeakerIcon className="h-4 w-4 text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-300">
              Backend unavailable — displaying mock data for preview purposes
            </span>
          </div>
        )}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cases" element={<CaseDashboard />} />
          <Route path="/cases/new" element={<CaseCreate />} />
          <Route path="/cases/:caseId" element={<CaseDetail isAdmin={user?.isAdmin} />} />
          <Route path="/settings/service-ips" element={<ServiceIPManager />} />
          <Route path="/settings/known-apps" element={<KnownAppManager />} />
          <Route path="/settings/aitm-trusted-domains" element={<AitmTrustedDomainsManager />} />
          <Route path="/settings/aitm-trusted-pairs" element={<AitmTrustedPairsManager />} />
          <Route path="/settings/known-malicious-ips" element={<KnownMaliciousIPsManager />} />
          <Route path="/settings/known-malicious-ips/search/:jobId" element={<IpActivitySearchResultsPage />} />
          <Route path="/settings/mailbox-pair-allowlist" element={<MailboxPairAllowlistManager />} />
          <Route path="/settings/account-allowlist" element={<AccountAllowlistManager />} />
          <Route path="/settings/teams-phone-models" element={<TeamsPhoneModelsManager />} />
          <Route path="/settings/defender-file-hash-allowlist" element={<DefenderFileHashAllowlistManager />} />
          <Route path="/settings/benign-ip-allowlist" element={<DefenderPassthroughIpAllowlistManager />} />
          <Route path="/settings/user-app-pair-allowlist" element={<UserAppPairAllowlistManager />} />
          <Route path="/settings/group-app-allowlist" element={<GroupAppAllowlistManager />} />
          <Route path="/settings/spray-origin-watchlist" element={<SprayOriginIPWatchlistManager />} />
          <Route path="/settings/hidden-ops" element={<HiddenOperations />} />
          <Route path="/release-notes" element={<ReleaseNotes />} />
          <Route path="/guide" element={<GuideHome />} />
          <Route path="/guide/alerts" element={<AlertsGuide />} />
          <Route path="/alerts" element={<AlertsPage />} />
          {/* Deep link from the Notification-Dispatcher emails/tickets: opens one
              alert by tenant + alert id. Same component; AlertsPage reads useParams. */}
          <Route path="/alerts/:tenantId/:alertId" element={<AlertsPage />} />
          <Route path="/recent-events" element={<RecentEventsPage />} />
          <Route path="/ip-search" element={<IpAccountSearchPage />} />
          <Route path="/admin/tenants" element={<AdminTenantsHub />} />
          <Route path="/admin/tenants/m365" element={<TenantManagement />} />
          <Route path="/admin/tenants/bpc" element={<BpcTenantsManagement />} />
          <Route path="/admin/alert-rules" element={<AlertRulesPage />} />
          <Route path="/admin/msp-notifications" element={<MspNotificationSettingsManager />} />
          <Route path="/admin/ticketing-config" element={<TicketingConfigManager />} />
          <Route path="/admin/tenant-ticketing-routes" element={<TenantTicketingRoutingManager />} />
          <Route path="/admin/psa-user-mapping" element={<PortalUserPsaMappingManager />} />
          <Route path="/admin/webhooks" element={<WebhookConfigManager />} />
          <Route path="/admin/test-results/:ruleId/:jobId" element={<TestRunResultsPage />} />
          <Route path="/admin/investigations-avoided" element={<InvestigationsAvoidedDashboard />} />
          <Route path="/admin/tenant-cost" element={<TenantCostDashboard />} />
          <Route path="/admin/slo" element={<SloDashboard />} />
          <Route path="/admin/activity-log" element={<ActivityLogPage />} />
          <Route path="/coverage" element={<CoverageHub />} />
          <Route path="/coverage/devices" element={<CoverageAnalysisPage />} />
          <Route path="/coverage/comparison" element={<CoverageComparisonPage />} />
          <Route path="/coverage/entra-identity" element={<EntraIdentityPage />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Exit-Demo-Mode confirmation + delayed reveal.
 *
 * Entering Demo Mode is instant (the safe direction). LEAVING it puts real
 * customer data back on screen, so it takes an explicit confirmation followed by
 * a visible countdown that can be cancelled at any point — enough time to stop
 * screen sharing. Closing the modal any way (X / backdrop / Cancel) aborts and
 * leaves Demo Mode ON.
 */
function ExitDemoModal({ onClose }) {
  // Absolute deadline (ms) once confirmed, rather than a countdown that decrements
  // itself. A chained setTimeout drifts, and browsers throttle or freeze timers in
  // a background tab — which this flow invites, because it tells the presenter to
  // go and stop screen sharing while it runs. Recomputing from the clock means the
  // exit still lands when they come back to the tab.
  const [deadline, setDeadline] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(EXIT_DEMO_COUNTDOWN_SECONDS);
  // Set once the flag is actually cleared. Separated from the reload so a reload
  // that never happens can't leave the browser stuck in Demo Mode — the whole
  // failure this modal was reported for.
  const [exited, setExited] = useState(false);

  useEffect(() => {
    if (deadline === null || exited) return undefined;
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setDemoMode(false);
        setExited(true);
      }
    };
    tick();
    const id = setInterval(tick, 250);
    // Catch up immediately on return to the tab, in case the interval was frozen.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [deadline, exited]);

  // Reload in its own effect: if this fails or is blocked (an unsaved-changes
  // prompt, say), Demo Mode is already off and the panel below offers a manual
  // reload — the user is never stranded.
  useEffect(() => {
    if (!exited) return undefined;
    window.location.reload();
    return undefined;
  }, [exited]);

  const counting = deadline !== null && !exited;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4"
      onClick={exited ? undefined : onClose}
    >
      <div
        className="bg-gray-900 border border-amber-600/50 rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold text-amber-300 flex items-center gap-2">
            <BeakerIcon className="h-5 w-5" />
            {exited ? 'Demo Mode is off' : 'Exit Demo Mode?'}
          </h3>
          {!exited && (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1" title="Cancel">
              <XMarkIconSolid />
            </button>
          )}
        </div>

        {exited ? (
          <>
            <p className="text-sm text-gray-300">
              Demo Mode has been switched off. Reloading to show real data…
            </p>
            <p className="text-sm text-gray-400 mt-2">
              If this page does not refresh on its own, reload it — Demo Mode is already off, so
              a refresh from anywhere will bring back real data.
            </p>
            <div className="flex justify-end mt-5">
              <button onClick={() => window.location.reload()} className="btn-primary text-sm">
                Reload now
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-300">
              Exiting Demo Mode will display <span className="font-semibold text-red-300">REAL customer data</span>{' '}
              — names, email addresses, IP addresses, tenants and email subjects.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Stop screen sharing first. After you confirm there is a{' '}
              {EXIT_DEMO_COUNTDOWN_SECONDS}-second delay before real data reappears; you can cancel at
              any point during the countdown.
            </p>

            {counting && (
              <div className="mt-4 rounded border border-amber-600/40 bg-amber-900/20 px-3 py-2 text-center">
                <p className="text-sm font-semibold text-amber-300">
                  Restoring real data in {secondsLeft}s…
                </p>
                <p className="text-xs text-amber-400/80 mt-0.5">The page will reload automatically.</p>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onClose} className="btn-secondary text-sm">
                {counting ? 'Cancel — stay in Demo Mode' : 'Cancel'}
              </button>
              {!counting && (
                <button
                  onClick={() => {
                    setSecondsLeft(EXIT_DEMO_COUNTDOWN_SECONDS);
                    setDeadline(Date.now() + EXIT_DEMO_COUNTDOWN_SECONDS * 1000);
                  }}
                  className="btn-danger text-sm"
                >
                  Exit Demo Mode
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Tiny inline close glyph — App.jsx imports only outline icons, and pulling in
// XMarkIcon here would be the sole addition for one button.
function XMarkIconSolid() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}
